import React, { useState } from 'react';

import { Box, Button } from '@mui/material';
import { OrdTransferList } from '@ragu-components/ord-components';
import { Resources } from '@ragu-components/ord-resources';
import { useGetRecordRepresentation, useNotify, useRefresh, useTranslate } from 'react-admin';

import { useQueueRecordingConfsData } from './hooks/useQueueRecordingConfsData';
import { useQueueRecordingConfsOperations } from './hooks/useQueueRecordingConfsOperations';

const hasListChanged = (initial: string[], current: string[]): boolean => {
  if (initial.length !== current.length) return true;
  const set = new Set(initial);
  return current.some((id) => !set.has(id));
};

export const AssignQueueRecordingConfsForm = ({
  confId,
  onUpdate = (): void => {},
}: {
  confId: string;
  onUpdate?: () => void;
}): React.ReactElement => {
  const translate = useTranslate();
  const refresh = useRefresh();
  const notify = useNotify();

  const { assignedQueueIds, loaded } = useQueueRecordingConfsData(confId);
  const { saveQueues } = useQueueRecordingConfsOperations();

  const [currentQueueIds, setCurrentQueueIds] = useState<string[]>([]);
  const [updateDisabled, setUpdateDisabled] = useState(true);
  const [saving, setSaving] = useState(false);

  const initialQueueIds = React.useRef<string[]>([]);

  const queueRepresentation = useGetRecordRepresentation(Resources.queues);

  React.useEffect(() => {
    if (loaded) {
      initialQueueIds.current = assignedQueueIds;
      setCurrentQueueIds(assignedQueueIds);
      setUpdateDisabled(true);
    }
  }, [loaded, assignedQueueIds]);

  const handleQueuesChange = (selectedIds: string[]): void => {
    setCurrentQueueIds(selectedIds);
    setUpdateDisabled(!hasListChanged(initialQueueIds.current, selectedIds));
  };

  const handleSave = (): void => {
    setSaving(true);

    void (async () => {
      const result = await saveQueues(confId, currentQueueIds);

      if (result.success) {
        notify('ra.notification.updated', {
          type: 'success',
          messageArgs: { smart_count: Math.max(currentQueueIds.length, 1) },
        });
      } else {
        const errorsMessage = result.error !== undefined ? ` (${result.error})` : '';
        const msg = `${translate('ord.httpErrors.updateManyFailed')}${errorsMessage}`;
        notify(msg, { type: 'error', messageArgs: { _: msg } });
      }

      setSaving(false);
      refresh();
      onUpdate();
    })();
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, padding: 2, overflow: 'auto' }}>
      {loaded ? (
        <>
          <OrdTransferList
            key={JSON.stringify(currentQueueIds)}
            selectedIds={currentQueueIds}
            fiqlArgument='name'
            resource={Resources.queues}
            onChange={handleQueuesChange}
            optionText={queueRepresentation}
          />
          <Button onClick={handleSave} disabled={updateDisabled || saving} variant='contained'>
            {translate('ra.action.save')}
          </Button>
        </>
      ) : null}
    </Box>
  );
};
